import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Loader2, Mail, Lock, ArrowRight, AlertCircle, CheckCircle, Eye, EyeOff,
  User, Building2, Key,
} from 'lucide-react';
import { signIn, signUp, signInWithGoogle, resetPassword, resendVerificationEmail } from '../lib/auth';
import { isValidCodeFormat } from '../lib/validate';
import { checkCode } from '../lib/codesClient';
import { setPendingCode } from '../lib/pendingCode';
import { bindPendingCode } from '../lib/bindPendingCode';
import { useAuth } from '../hooks/useAuth';
import { auth } from '../lib/firebase';

/**
 * Email + password + Google auth form, with sign-in / sign-up tabs, password
 * reset, and post-signup email-verify state. Extracted from Login.jsx so it
 * can be embedded inline on /activate without forcing the user off-page.
 *
 * Behavior:
 *   - The parent owns navigation. After a successful sign-in we fire
 *     `onSignedIn()` if provided; otherwise we no-op and let the parent
 *     re-render off useAuth updates.
 *   - After a successful sign-up we show a verify-email screen with a
 *     resend button + "Already verified? Sign in" CTA that flips to the
 *     signin tab. `onSignedUp({ email })` fires too if you want to react.
 *   - Google sign-in calls `onSignedIn()` once the popup resolves.
 *
 * Props:
 *   defaultTab    — 'signin' | 'signup' (default 'signin')
 *   onSignedIn    — () => void after successful sign-in (incl. Google)
 *   onSignedUp    — ({ email }) => void after a successful sign-up
 *   showCodeFootnote — boolean, render the "Have a code? Enter it here" link
 *                      (Login.jsx wants it; Activate.jsx doesn't)
 *   continueWithoutAccount — JSX for the bottom "continue without signing in"
 *                            note (or null to hide). Login.jsx-only.
 */
export default function AuthForm({
  defaultTab = 'signin',
  onSignedIn,
  onSignedUp,
  showCodeFootnote = false,
  continueWithoutAccount = null,
}) {
  const [tab, setTab] = useState(defaultTab);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [hasCode, setHasCode] = useState(false);
  const [code, setCode] = useState('');
  const [codeStatus, setCodeStatus] = useState({ state: 'idle' }); // idle|checking|ok|bad

  const [verifyState, setVerifyState] = useState(null); // { email } after a successful signup
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownRef = useRef(null);
  const [verifyChecking, setVerifyChecking] = useState(false);
  const [verifyMsg, setVerifyMsg] = useState(null);

  const { refreshAccount } = useAuth();

  const [resetOpen, setResetOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);

  useEffect(() => () => {
    if (cooldownRef.current) clearInterval(cooldownRef.current);
  }, []);

  // After signup the user is ALREADY signed in (just unverified) — so we don't
  // re-sign-in. We poll the live Firebase user for emailVerified, bind any code
  // stashed at signup, refresh the account, then proceed. This is the fix for
  // the desktop/login path: the old "Already verified? Sign in" did a same-user
  // re-sign-in, which does NOT fire onAuthStateChanged, so the code never bound.
  // (/activate already worked this way via its own VerifyEmailScreen.)
  const checkVerifiedAndBind = useCallback(async ({ silent = false } = {}) => {
    const u = auth.currentUser;
    if (!u) return;
    if (!silent) { setVerifyChecking(true); setVerifyMsg(null); }
    try {
      await u.reload();
      if (u.emailVerified) {
        await bindPendingCode();                 // binds a stashed code; no-op if none
        try { await refreshAccount({ forceTokenRefresh: true }); } catch { /* non-fatal */ }
        if (onSignedIn) onSignedIn();             // already signed in — just navigate
      } else if (!silent) {
        setVerifyMsg("Still not verified. Click the link in your email, then try again.");
      }
    } catch {
      if (!silent) setVerifyMsg('Could not check verification. Please try again.');
    } finally {
      if (!silent) setVerifyChecking(false);
    }
  }, [onSignedIn, refreshAccount]);

  // Auto-poll every 5 s while on the verify screen, so the user doesn't have to
  // click anything once they open the link in their email.
  useEffect(() => {
    if (!verifyState) return undefined;
    const id = setInterval(() => checkVerifiedAndBind({ silent: true }), 5000);
    return () => clearInterval(id);
  }, [verifyState, checkVerifiedAndBind]);

  // Inline activation-code validation: format-check locally, then debounce a
  // /codes/check call. Advisory only — the real bind happens after email verify.
  useEffect(() => {
    if (!hasCode || !code) { setCodeStatus({ state: 'idle' }); return; }
    const trimmed = code.toUpperCase().trim();
    if (!isValidCodeFormat(trimmed)) {
      setCodeStatus({ state: 'bad', message: 'Check the format: SCRV-XXXX-XXXX-XXXX' });
      return;
    }
    setCodeStatus({ state: 'checking' });
    const t = setTimeout(async () => {
      const r = await checkCode(trimmed);
      if (!r) { setCodeStatus({ state: 'idle' }); return; }
      if (r.ok && r.status === 'unused') setCodeStatus({ state: 'ok', message: `Valid ${tierLabel(r.tier)} license — verify your email to activate` });
      else if (r.ok && r.status === 'activated') setCodeStatus({ state: 'bad', message: 'That code is already in use.' });
      else if (r.reason === 'revoked') setCodeStatus({ state: 'bad', message: 'That code is no longer valid.' });
      else setCodeStatus({ state: 'bad', message: "We don't recognize that code — check for typos." });
    }, 450);
    return () => clearTimeout(t);
  }, [hasCode, code]);

  function switchToSignIn(prefill) {
    setTab('signin');
    if (prefill) setEmail(prefill);
    setError(null);
    setVerifyState(null);
    setResetOpen(false);
  }

  function switchToSignUp() {
    setTab('signup');
    setError(null);
    setVerifyState(null);
    setResetOpen(false);
  }

  function openReset() {
    setResetOpen(true);
    setResetEmail(email);
    setResetSent(false);
    setError(null);
  }

  function makeErrorProps() {
    return {
      onSwitchToSignIn: (e) => switchToSignIn(e),
      onForgotPassword: openReset,
      onSwitchToSignUp: switchToSignUp,
      prefillEmail: email,
    };
  }

  async function handleSignIn(e) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await signIn(email.trim(), password);
      if (onSignedIn) onSignedIn();
    } catch (err) {
      setError(translateFirebaseError(err, makeErrorProps()));
    } finally {
      setLoading(false);
    }
  }

  async function handleSignUp(e) {
    e.preventDefault();
    setError(null);
    const trimmedFirst = firstName.trim();
    if (!trimmedFirst) {
      setError('First name is required.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await signUp(email.trim(), password, {
        firstName: trimmedFirst,
        lastName: lastName.trim(),
        companyName: companyName.trim(),
      });
      // Stash a format-valid code; it binds after email verification (useAuth).
      if (hasCode && isValidCodeFormat(code)) setPendingCode(code.toUpperCase().trim());
      const cleanEmail = email.trim();
      setVerifyState({ email: cleanEmail });
      startResendCooldown();
      if (onSignedUp) onSignedUp({ email: cleanEmail });
    } catch (err) {
      setError(translateFirebaseError(err, makeErrorProps()));
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogle() {
    setError(null);
    setLoading(true);
    try {
      await signInWithGoogle();
      if (onSignedIn) onSignedIn();
    } catch (err) {
      setError(translateFirebaseError(err, makeErrorProps()));
    } finally {
      setLoading(false);
    }
  }

  async function handleReset(e) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await resetPassword(resetEmail.trim());
      setResetSent(true);
    } catch (err) {
      setError(translateFirebaseError(err));
    } finally {
      setLoading(false);
    }
  }

  function startResendCooldown() {
    setResendCooldown(30);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setResendCooldown((n) => {
        if (n <= 1) {
          clearInterval(cooldownRef.current);
          return 0;
        }
        return n - 1;
      });
    }, 1000);
  }

  async function handleResendVerification() {
    if (resendCooldown > 0) return;
    try {
      await resendVerificationEmail();
      startResendCooldown();
    } catch {
      /* user may not be signed in yet — ignore */
    }
  }

  // Post-signup verify screen. Auto-polls for verification and binds any code
  // stashed at signup, then proceeds — no re-sign-in (see checkVerifiedAndBind).
  if (verifyState) {
    return (
      <div className="p-6 text-center">
        <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-6 h-6 text-green-500" />
        </div>
        <h2 className="text-base font-semibold text-gray-900 mb-2">Check your email</h2>
        <p className="text-sm text-gray-600 mb-6">
          We sent a verification link to{' '}
          <span className="font-medium text-gray-800 break-all">{verifyState.email}</span>.
          Click the link, then come back here.
        </p>

        {verifyMsg && (
          <p role="status" className="mb-4 text-sm rounded-lg px-3 py-2 bg-amber-50 text-amber-700">
            {verifyMsg}
          </p>
        )}

        <div className="space-y-2">
          <button
            type="button"
            onClick={() => checkVerifiedAndBind()}
            disabled={verifyChecking}
            className="w-full px-4 py-3 sm:py-2.5 bg-brand-blue text-white rounded-lg text-sm font-medium hover:bg-brand-dark disabled:opacity-50 flex items-center justify-center gap-2 min-h-[44px]"
          >
            {verifyChecking ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                Checking…
              </>
            ) : (
              "I've verified my email"
            )}
          </button>
          <button
            type="button"
            onClick={handleResendVerification}
            disabled={resendCooldown > 0}
            className="w-full px-4 py-3 sm:py-2.5 border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 min-h-[44px]"
          >
            {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend verification email'}
          </button>
          <button
            type="button"
            onClick={() => switchToSignIn(verifyState.email)}
            className="w-full px-4 py-2 text-xs text-gray-500 hover:text-gray-700 min-h-[44px]"
          >
            Use a different account
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Tabs */}
      <div className="flex border-b border-gray-100">
        {[
          { id: 'signin', label: 'Sign in' },
          { id: 'signup', label: 'Create account' },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => {
              setTab(t.id);
              setError(null);
              setVerifyState(null);
              setResetOpen(false);
            }}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              tab === t.id
                ? 'text-brand-blue border-b-2 border-brand-blue bg-brand-light/30'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="p-6">
        {resetOpen ? (
          <ResetPasswordForm
            email={resetEmail}
            setEmail={setResetEmail}
            loading={loading}
            error={error}
            sent={resetSent}
            onSubmit={handleReset}
            onCancel={() => { setResetOpen(false); setResetSent(false); setError(null); }}
            onSignIn={() => switchToSignIn()}
          />
        ) : (
          <>
            <button
              type="button"
              onClick={handleGoogle}
              disabled={loading}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 sm:py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 min-h-[44px]"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
              ) : (
                <GoogleIcon />
              )}
              {tab === 'signin' ? 'Sign in with Google' : 'Sign up with Google'}
            </button>

            <div className="flex items-center gap-3 my-4 text-xs text-gray-400">
              <div className="h-px flex-1 bg-gray-100" />
              <span>or</span>
              <div className="h-px flex-1 bg-gray-100" />
            </div>

            <form
              onSubmit={tab === 'signin' ? handleSignIn : handleSignUp}
              className="space-y-3"
            >
              {tab === 'signup' && (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label htmlFor="authform-first" className="sr-only">First name (required)</label>
                      <Field icon={User}>
                        <input
                          id="authform-first"
                          type="text"
                          required
                          autoComplete="given-name"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          placeholder="First name *"
                          className="w-full outline-none text-base sm:text-sm bg-transparent"
                          disabled={loading}
                          maxLength={100}
                        />
                      </Field>
                    </div>
                    <div>
                      <label htmlFor="authform-last" className="sr-only">Last name (optional)</label>
                      <Field icon={User}>
                        <input
                          id="authform-last"
                          type="text"
                          autoComplete="family-name"
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          placeholder="Last name"
                          className="w-full outline-none text-base sm:text-sm bg-transparent"
                          disabled={loading}
                          maxLength={100}
                        />
                      </Field>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="authform-company" className="sr-only">Company (optional)</label>
                    <Field icon={Building2}>
                      <input
                        id="authform-company"
                        type="text"
                        autoComplete="organization"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        placeholder="Company (optional)"
                        className="w-full outline-none text-base sm:text-sm bg-transparent"
                        disabled={loading}
                        maxLength={200}
                      />
                    </Field>
                  </div>
                </>
              )}

              <div>
                <label htmlFor="authform-email" className="sr-only">Email address</label>
                <Field icon={Mail}>
                  <input
                    id="authform-email"
                    type="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full outline-none text-base sm:text-sm bg-transparent"
                    disabled={loading}
                    aria-describedby={error ? 'authform-error' : undefined}
                  />
                </Field>
              </div>

              <div>
                <label htmlFor="authform-password" className="sr-only">Password</label>
                <Field icon={Lock}>
                  <input
                    id="authform-password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    autoComplete={tab === 'signin' ? 'current-password' : 'new-password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Password"
                    className="w-full outline-none text-base sm:text-sm bg-transparent"
                    disabled={loading}
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="-mr-2 -my-3 sm:-my-2.5 px-3 py-3 text-gray-400 hover:text-gray-600 rounded-md inline-flex items-center justify-center min-w-[44px] min-h-[44px]"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    tabIndex={0}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </Field>
                {tab === 'signup' && password && (
                  <p className={`mt-1 text-xs px-1 ${strengthColor[passwordStrength(password)]}`}>
                    {strengthLabel[passwordStrength(password)]}
                  </p>
                )}
              </div>

              {tab === 'signup' && (
                <div>
                  <label htmlFor="authform-confirm" className="sr-only">Confirm password</label>
                  <Field icon={Lock}>
                    <input
                      id="authform-confirm"
                      type={showPassword ? 'text' : 'password'}
                      required
                      autoComplete="new-password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm password"
                      className="w-full outline-none text-base sm:text-sm bg-transparent"
                      disabled={loading}
                      minLength={6}
                    />
                  </Field>
                </div>
              )}

              {tab === 'signup' && (
                <div className="pt-1">
                  <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={hasCode}
                      onChange={(e) => { setHasCode(e.target.checked); if (!e.target.checked) { setCode(''); setCodeStatus({ state: 'idle' }); } }}
                      className="rounded border-gray-300 text-brand-blue focus:ring-brand-blue"
                      disabled={loading}
                    />
                    I have an activation code
                  </label>
                  {hasCode && (
                    <div className="mt-2">
                      <Field icon={Key}>
                        <input
                          type="text"
                          autoComplete="off"
                          value={code}
                          onChange={(e) => setCode(e.target.value.toUpperCase())}
                          placeholder="SCRV-XXXX-XXXX-XXXX"
                          maxLength={19}
                          className="w-full outline-none text-base sm:text-sm bg-transparent tracking-wide"
                          disabled={loading}
                        />
                      </Field>
                      {codeStatus.state === 'checking' && (
                        <p className="mt-1 text-xs text-gray-400 px-1">Checking…</p>
                      )}
                      {codeStatus.state === 'ok' && (
                        <p className="mt-1 text-xs text-green-600 px-1">✓ {codeStatus.message}</p>
                      )}
                      {codeStatus.state === 'bad' && (
                        <p className="mt-1 text-xs text-amber-600 px-1">{codeStatus.message}</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {error && (
                <div
                  id="authform-error"
                  role="alert"
                  className="flex items-start gap-2 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2"
                >
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full px-4 py-3 sm:py-2.5 bg-brand-blue text-white rounded-lg text-sm font-medium hover:bg-brand-dark disabled:opacity-50 flex items-center justify-center gap-2 min-h-[44px]"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                    {tab === 'signin' ? 'Signing in…' : 'Creating account…'}
                  </>
                ) : (
                  <>
                    {tab === 'signin' ? 'Sign in' : 'Create account'}
                    <ArrowRight className="w-4 h-4" aria-hidden="true" />
                  </>
                )}
              </button>
            </form>

            {tab === 'signin' && (
              <button
                type="button"
                onClick={openReset}
                className="mt-3 -mx-2 px-3 py-3 text-xs text-gray-500 hover:text-gray-700 rounded-md min-h-[44px]"
              >
                Forgot password?
              </button>
            )}

            {showCodeFootnote && (
              <p className="mt-3 text-center text-xs text-gray-400 leading-loose">
                Have a Scrivar registration code?{' '}
                <a href="/activate" className="text-gray-600 hover:text-gray-800 underline inline-block py-2 px-1">
                  Enter it here
                </a>
              </p>
            )}

            {continueWithoutAccount}
          </>
        )}
      </div>
    </>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function translateFirebaseError(err, opts = {}) {
  const code = err?.code || '';
  const { onSwitchToSignIn, onForgotPassword, onSwitchToSignUp, prefillEmail } = opts;

  if (code.includes('email-already-in-use')) {
    return (
      <span>
        There&rsquo;s already a Scrivar account with that email.{' '}
        {onSwitchToSignIn && (
          <button
            type="button"
            className="underline font-medium hover:text-red-800"
            onClick={() => onSwitchToSignIn(prefillEmail)}
          >
            Sign in instead?
          </button>
        )}
      </span>
    );
  }
  if (code.includes('invalid-email')) {
    return "That doesn't look like a valid email address.";
  }
  if (code.includes('weak-password')) {
    return 'Password needs to be at least 6 characters. Longer is better.';
  }
  if (code.includes('wrong-password') || code.includes('invalid-credential')) {
    return (
      <span>
        Wrong email or password.{' '}
        {onForgotPassword && (
          <button
            type="button"
            className="underline font-medium hover:text-red-800"
            onClick={onForgotPassword}
          >
            Forgot password?
          </button>
        )}
      </span>
    );
  }
  if (code.includes('user-not-found')) {
    return (
      <span>
        No Scrivar account with that email.{' '}
        {onSwitchToSignUp && (
          <button
            type="button"
            className="underline font-medium hover:text-red-800"
            onClick={onSwitchToSignUp}
          >
            Create one?
          </button>
        )}
      </span>
    );
  }
  if (code.includes('too-many-requests')) {
    return (
      <span>
        Too many sign-in attempts. Wait a few minutes, or{' '}
        {onForgotPassword && (
          <button
            type="button"
            className="underline font-medium hover:text-red-800"
            onClick={onForgotPassword}
          >
            reset your password
          </button>
        )}{' '}
        if you&rsquo;ve forgotten it.
      </span>
    );
  }
  if (code.includes('popup-closed-by-user')) {
    return 'Google sign-in was cancelled.';
  }
  if (code.includes('network-request-failed')) {
    return "Can't reach Scrivar right now. Check your internet connection and try again.";
  }
  if (code.includes('cancelled-popup-request')) {
    return 'Google sign-in was cancelled.';
  }
  return err?.message || 'Something went wrong. Please try again.';
}

function tierLabel(tier) {
  if (tier === 'pro') return 'Pro';
  if (tier === 'lite') return 'Lite';
  return '';
}

function passwordStrength(pw) {
  if (!pw) return null;
  if (pw.length >= 12 && /[0-9]/.test(pw)) return 'strong';
  if (pw.length >= 8) return 'good';
  return 'weak';
}

const strengthLabel = { weak: 'At least 6 characters', good: 'Good', strong: 'Strong' };
const strengthColor = { weak: 'text-gray-400', good: 'text-green-600', strong: 'text-green-600' };

function Field({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-2 px-3 py-3 sm:py-2.5 border border-gray-200 rounded-lg focus-within:border-brand-blue focus-within:ring-2 focus-within:ring-brand-blue/20">
      <Icon className="w-4 h-4 text-gray-400 shrink-0" aria-hidden="true" />
      {children}
    </div>
  );
}

function ResetPasswordForm({ email, setEmail, loading, error, sent, onSubmit, onCancel, onSignIn }) {
  if (sent) {
    return (
      <div className="text-center py-2">
        <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-3" aria-hidden="true" />
        <p className="text-sm font-medium text-gray-900 mb-2">Check your email</p>
        <p className="text-sm text-gray-600 mb-4">
          A reset link is on its way. Click it to choose a new password.
        </p>
        <button
          type="button"
          onClick={onSignIn}
          className="px-3 py-2 text-sm text-brand-blue hover:text-brand-dark min-h-[44px]"
        >
          Back to sign in
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-3">
      <p className="text-sm text-gray-600">
        Enter your email and we&rsquo;ll send you a password reset link.
      </p>
      <div>
        <label htmlFor="reset-email" className="sr-only">Email address</label>
        <div className="flex items-center gap-2 px-3 py-3 sm:py-2.5 border border-gray-200 rounded-lg focus-within:border-brand-blue">
          <Mail className="w-4 h-4 text-gray-400" aria-hidden="true" />
          <input
            id="reset-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="w-full outline-none text-base sm:text-sm bg-transparent"
            disabled={loading}
            aria-describedby={error ? 'reset-error' : undefined}
          />
        </div>
      </div>
      {error && (
        <div
          id="reset-error"
          role="alert"
          className="flex items-start gap-2 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2"
        >
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-3 sm:py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 min-h-[44px]"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="flex-1 px-4 py-3 sm:py-2.5 bg-brand-blue text-white rounded-lg text-sm font-medium hover:bg-brand-dark disabled:opacity-50 flex items-center justify-center gap-2 min-h-[44px]"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : 'Send reset link'}
        </button>
      </div>
    </form>
  );
}

function GoogleIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.56c2.08-1.92 3.28-4.74 3.28-8.1z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.77c-.99.66-2.25 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.43.34-2.09V7.07H2.18A11 11 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.2 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
    </svg>
  );
}
